import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { mergeSessionProgress } from "@/lib/academy.functions";
import { readSessionId } from "@/lib/academy-session";

export type AppRole = "client" | "agent" | "admin" | "super_admin" | "staff";

interface Profile {
  id: string;
  full_name: string | null;
  email: string | null;
  status: string | null;
}

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  role: AppRole | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

async function logActivity(action: string, userId: string | null) {
  try {
    await supabase.from("activity_logs").insert({
      user_id: userId,
      action,
      target_type: "auth",
    });
  } catch {
    /* non-blocking */
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [loading, setLoading] = useState(true);

  const loadProfileAndRole = async (uid: string) => {
    const [{ data: p }, { data: r }] = await Promise.all([
      supabase.from("profiles").select("id, full_name, email, status").eq("id", uid).maybeSingle(),
      supabase.rpc("get_primary_role", { _user_id: uid }),
    ]);
    setProfile((p as Profile) ?? null);
    setRole((r as AppRole) ?? null);
  };

  useEffect(() => {
    let initialized = false;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      if (s?.user) {
        setLoading(true);
        setTimeout(() => {
          void loadProfileAndRole(s.user.id).finally(() => setLoading(false));
        }, 0);
        if (event === "SIGNED_IN") {
          void logActivity("login", s.user.id);
          const sid = readSessionId();
          if (sid) {
            void mergeSessionProgress({ data: { sessionId: sid } }).catch(() => {});
          }
        }
      } else {
        setProfile(null);
        setRole(null);
        // Don't flip loading=false until the initial getSession() has resolved,
        // otherwise a stray INITIAL_SESSION-with-null fires a premature redirect
        // to /login before storage rehydration completes.
        if (initialized) setLoading(false);
      }
    });

    supabase.auth.getSession().then(async ({ data }) => {
      setSession(data.session);
      if (data.session?.user) {
        await loadProfileAndRole(data.session.user.id);
      }
      initialized = true;
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      profile,
      role,
      loading,
      signOut: async () => {
        if (session?.user) await logActivity("logout", session.user.id);
        await supabase.auth.signOut();
      },
      refresh: async () => {
        if (session?.user) await loadProfileAndRole(session.user.id);
      },
    }),
    [session, profile, role, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}